import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Typography,
  Tabs,
  Tab,
  Box,
  Divider,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import EditAsset from "../Assets/EditAsset";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import moment from "moment";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MUIDataTable from "mui-datatables";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface Asset {
  last_visited: string | number | Date;
  asset_service_id: string;
  asset_type_name: string;
  asset_name: string;
  state_name: string;
  city_name: string;
  region_name: string;
  address_type: string;
  address: string;
  customer_name: string;
  customer_phone: string;
  subscription_creation_date: string | Date;
  subscription_type: string;
  subscription_start_date: string | Date;
  subscription_end_date: string | Date;
  asset_image: string;
  address_id: string;
  customer_id: string;
  asset_purchased_date: string | number | Date;
  asset_type_id: string;
  asset_id: string;
  image_type: string;
  division_name: string;
  division_id: string;
  type: string;
  asset_sku: string;
}

interface Ticket {
  customer_address: string;
  customer_name: string;
  title: string;
  asset_name: string;
  address: string;
  assignee: string;
  created_at: string | Date;
}

interface Sub {
  subscription_id?: number; // Added for unique key
  subscription_type_name: string | Date;
  created_at: string | Date;
  end_date: string | Date;
  is_active: boolean;
}

interface AssetDetailItem {
  label: string;
  value: string | number;
  key: string; // For unique keys
}

// Extracted component for address tooltip
interface AddressTooltipProps {
  value: string;
}

const AddressTooltip: React.FC<AddressTooltipProps> = ({ value }) => {
  return (
    <Tooltip title={value}>
      <span>{value.length > 20 ? `${value.substring(0, 20)}...` : value}</span>
    </Tooltip>
  );
};

// Extracted component for action buttons
interface ActionButtonProps {
  ticketId: number;
  onNavigate: (ticketId: number) => void;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  ticketId,
  onNavigate,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Tooltip title="View Ticket">
        <IconButton aria-label="view" onClick={() => onNavigate(ticketId)}>
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const AdminViewAsset = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchError } = useSnackbar();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  const fetchAsset = async () => {
    setLoading(true);
    try {
      const [assetRes, subsRes] = await Promise.all([
        axios.get(`/assets/${id}`),
        axios.get(`/subscription/assets/${id}`),
      ]);
      setAsset(assetRes.data);
      setSubscriptions(subsRes.data?.list ?? []);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response?.data?.error ?? error.message);
      }

      console.error(error);
      navigate("/admin/assets-list");
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    setLoadingTickets(true);
    try {
      const res = await axios.get(`/tickets/asset/${id}`);
      setTickets(res.data?.list ?? []);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response?.data?.error ?? error.message);
      }
    } finally {
      setLoadingTickets(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchAsset();
      fetchTickets();
    }
  }, [id]);

  const handleTabChange = (e: any, newVal: React.SetStateAction<number>) =>
    setTabIndex(newVal);

  const handleMediaOpen = (fileName: string) => {
    const url = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${fileName}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleTicketNavigation = (ticketId: number) => {
    navigate(`/admin/ticket-view/${ticketId}`);
  };

  // Fixed: Render functions for custom cells
  const renderAddressCell = (value: string) => {
    return <AddressTooltip value={value} />;
  };

  const renderActionCell = (ticketId: number) => {
    return (
      <ActionButton ticketId={ticketId} onNavigate={handleTicketNavigation} />
    );
  };

  // Fixed: Create asset details with unique keys
  const getAssetDetails = (): AssetDetailItem[] => {
    if (!asset) return [];

    const baseDetails: AssetDetailItem[] = [
      { label: "Asset Id", value: asset.asset_service_id, key: "asset_id" },
      { label: "Asset Type", value: asset.asset_type_name, key: "asset_type" },
      { label: "Brand Name", value: asset.asset_name, key: "brand_name" },
      {
        label: " Customer Address",
        value: `${asset.address},${asset.region_name}, ${asset.city_name}, ${asset.state_name}, ${asset.address_type}`,
        key: "customer_address",
      },
      {
        label: "Customer Name",
        value: asset.customer_name,
        key: "customer_name",
      },
      {
        label: "Customer Phone",
        value: asset.customer_phone,
        key: "customer_phone",
      },
      {
        label: "Subscription Creation",
        value: asset.subscription_creation_date
          ? moment(asset.subscription_creation_date).format("MMM D, YYYY")
          : "No Subscription",
        key: "subscription_creation",
      },
      {
        label: "Subscription Type",
        value: asset.subscription_type
          ? asset.subscription_type
          : "No SubScription",
        key: "subscription_type",
      },
      {
        label: "Subscription Start Date",
        value: asset.subscription_start_date
          ? moment(asset.subscription_start_date).format("MMM D, YYYY")
          : "No Subscription",
        key: "subscription_start",
      },
      {
        label: "Subscription End Date",
        value: asset.subscription_end_date
          ? moment(asset.subscription_end_date).format("MMM D, YYYY")
          : "No Subscription",
        key: "subscription_end",
      },
      {
        label: "Lasted Visited on",
        value: asset.last_visited
          ? moment(asset.last_visited).format("MMM D, YYYY")
          : "No Tickets",
        key: "last_visited",
      },
    ];

    // Add division if company type
    if (asset.type === "company") {
      baseDetails.push({
        label: "Division",
        value: asset.division_name,
        key: "division",
      });
    }

    baseDetails.push({ label: "SKU", value: asset.asset_sku, key: "sku" });

    return baseDetails;
  };

  // Fixed: Render tickets section with proper conditional logic
  const renderTicketsSection = () => {
    if (loadingTickets) {
      return <div>Loading Tickets...</div>;
    }

    if (tickets.length === 0) {
      return null;
    }

    return (
      <>
        <Divider sx={{ height: 2, backgroundColor: "blue", mt: 3 }} />
        <ThemeProvider theme={getMuiTheme}>
          <MUIDataTable
            title={"Tickets"}
            data={tickets.map((ticket: Ticket) => ({
              ...ticket,
              title: ticket.title ?? "-",
              customer_name: ticket.customer_name ?? "-",
              asset_name: ticket.asset_name ?? "-",
              address: `${ticket.customer_address}, `,
              assignee: ticket.assignee ?? "Unassigned",
              created_at: moment(ticket.created_at).format("MMM D, YYYY"),
            }))}
            columns={[
              { name: "title", label: "Title" },
              { name: "ticket_service_id", label: "Service Id" },
              { name: "asset_name", label: "Asset Type" },
              {
                name: "address",
                label: "Address",
                options: {
                  customBodyRender: renderAddressCell,
                },
              },
              { name: "status_name", label: "Status" },
              { name: "assignee", label: "Assignee" },
              { name: "created_at", label: "Created At" },
              {
                name: "ticket_id",
                label: "Actions",
                options: {
                  filter: false,
                  sort: false,
                  customBodyRender: renderActionCell,
                },
              },
            ]}
            options={{
              selectableRows: "none",
              elevation: 0,
              download: true,
              print: true,
              filter: true,
              search: true,
              responsive: "standard",
            }}
          />
        </ThemeProvider>
      </>
    );
  };

  if (loading)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div>Loading...</div>
      </DashboardLayout>
    );

  if (!asset)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div>Asset not found</div>
      </DashboardLayout>
    );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card style={{ padding: "24px", width: "100%", borderRadius: "8px" }}>
        <Box sx={{ borderRadius: "8px", p: 1, mb: 3 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            textColor="inherit"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              "& .MuiTabs-flexContainer": { gap: "15px" },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#eee",
                color: "#008080",
                borderRadius: "8px",
                minHeight: "48px",
              },
              "& .MuiTab-root.Mui-selected": {
                backgroundColor: "#008080",
                color: "#fff !important",
              },
            }}
          >
            <Tab label="View Asset" />
            <Tab label="Edit Asset" />
          </Tabs>
        </Box>

        {tabIndex === 0 && (
          <>
            {/* Asset Details */}
            <Box
              sx={{
                border: "1px solid #008080",
                borderRadius: "5px",
                padding: "20px",
                mb: 2,
                boxShadow: "1px 1px 4px #008080",
              }}
            >
              <Grid container spacing={2}>
                {/* Fixed: Use unique keys instead of array index */}
                {getAssetDetails().map((item) => (
                  <Grid item xs={12} sm={6} key={item.key}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1rem",
                        lineHeight: 1.625,
                        color: "#344767",
                        margin: 0,
                      }}
                    >
                      <Box display="flex">
                        <span style={{ minWidth: "160px", fontWeight: 500 }}>
                          {item.label}
                        </span>
                        <span style={{ width: "10px" }}>:</span>
                        <span style={{ fontWeight: "normal" }}>
                          {item.value}
                        </span>
                      </Box>
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {subscriptions.length > 0 && (
              <>
                <Divider sx={{ height: 2, backgroundColor: "blue", mt: 1 }} />
                <Typography variant="h5" sx={{ mt: 1, mb: 2 }}>
                  Subscription History
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead sx={{ display: "table-row-group" }}>
                      <TableRow>
                        <TableCell>Subscription Type</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Fixed: Use subscription_id or created_at for unique keys */}
                      {subscriptions.map((sub: Sub, i) => (
                        <TableRow
                          key={sub.subscription_id ?? `${sub.created_at}-${i}`}
                        >
                          <TableCell>
                            {sub.subscription_type_name instanceof Date
                              ? sub.subscription_type_name.toLocaleDateString()
                              : sub.subscription_type_name}
                          </TableCell>
                          <TableCell>
                            {moment(sub.created_at).format("MMM D, YYYY")}
                          </TableCell>
                          <TableCell>
                            {moment(sub.end_date).format("MMM D, YYYY")}
                          </TableCell>
                          <TableCell>
                            {sub.is_active ? "Active" : "In-Active"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Fixed: Extracted nested ternary into function */}
            {renderTicketsSection()}

            {asset.asset_image && (
              <>
                <Typography variant="h5" sx={{ mt: 1, mb: 2 }}>
                  Asset Media
                </Typography>
                <Grid item xs={12} sx={{ mt: 2, textAlign: "center" }}>
                  {/* Fixed: Wrapped image in button for proper accessibility */}
                  <button
                    onClick={() => handleMediaOpen(asset.asset_image)}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "inline-block",
                    }}
                    aria-label="View full size asset image"
                  >
                    <img
                      src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${asset.asset_image}`}
                      alt="asset"
                      style={{
                        maxWidth: "100%",
                        borderRadius: 8,
                        display: "block",
                      }}
                    />
                  </button>
                </Grid>
              </>
            )}
          </>
        )}

        {tabIndex === 1 && (
          <Box sx={{ p: 3 }}>
            <EditAsset
              assetData={asset}
              fetchData={fetchAsset}
              onCancel={() => setTabIndex(0)}
            />
          </Box>
        )}
      </Card>
    </DashboardLayout>
  );
};

export default AdminViewAsset;
